import { JSONEditor } from '@json-editor/json-editor'

const BaseEditor = JSONEditor.defaults.editors.select

class SelectEditor extends BaseEditor {
  constructor (options, defaults) {
    super(options, defaults)
    this.isBasic = options.jsoneditor.options.isBasic || false
    this.displayInBasic = this.options?.displayInBasic || false
    this.readOnlyInBasic = this.options.readOnly || false
  }

  build () {
    super.build()
    if (this.isBasic && !this.displayInBasic) {
      this.options.hidden = true
    }
    if (this.isBasic && this.readOnlyInBasic) {
      this.disabled = true
      this.input.disabled = true
    }
  }

  setValue (value, initial) {
    /* Sanitize value before setting it */
    let sanitized = this.typecast(value)

    const haveToUseDefaultValue = !!this.jsoneditor.options.use_default_values || typeof this.schema.default !== 'undefined'

    const valueExistsInOptions = this.enum_values.includes(sanitized) // learning labs code
    if (
      (this.enum_values.length > 0 && !valueExistsInOptions) ||
      (initial && !this.isRequired() && !haveToUseDefaultValue)
    ) {
      /*  - learning labs code -  */
      if (!valueExistsInOptions) {
        console.warn(`WARNING: Select Editor at path ${this.path} has no option with value ${sanitized}, using first option instead`)
        this.valueThatDidntExistInOptions = sanitized
      }
      /*  ----------------------  */
      sanitized = this.enum_values[0]
    } else {
      /*  - learning labs code -  */
      // A valid value was found, so delete value that didn't exist:
      delete this.valueThatDidntExistInOptions
      /*  ----------------------  */
    }

    if (this.value === sanitized) return

    if (initial) this.is_dirty = false
    else if (this.jsoneditor.options.show_errors === 'change') this.is_dirty = true

    this.input.value = this.enum_options[this.enum_values.indexOf(sanitized)]

    this.value = sanitized
    this.onChange()
    this.change()
  }

  onWatchedFieldChange () {
    // console.log('SELECT onWatchedFieldChange:', this.path)
    let vars; let j
    let selectOptions = []; let selectTitles = []

    /* If this editor uses a dynamic select box */
    if (this.enumSource) {
      vars = this.getWatchedFieldValues()

      for (let i = 0; i < this.enumSource.length; i++) {
        /* Constant values */
        if (Array.isArray(this.enumSource[i])) {
          selectOptions = selectOptions.concat(this.enumSource[i])
          selectTitles = selectTitles.concat(this.enumSource[i])
        } else {
          let items = []
          /* Static list of items */
          if (Array.isArray(this.enumSource[i].source)) {
            items = this.enumSource[i].source
            /* A watched field */
          } else {
            items = vars[this.enumSource[i].source]
          }

          if (items) {
            /* Only use a predefined part of the array */
            if (this.enumSource[i].slice) {
              items = Array.prototype.slice.apply(items, this.enumSource[i].slice)
            }
            /* Filter the items */
            if (this.enumSource[i].filter) {
              const newItems = []
              for (j = 0; j < items.length; j++) {
                if (this.enumSource[i].filter({ i: j, item: items[j], watched: vars })) newItems.push(items[j])
              }
              items = newItems
            }

            const itemTitles = []
            const itemValues = []
            for (j = 0; j < items.length; j++) {
              const item = items[j]

              /* Rendered value */
              if (this.enumSource[i].value) {
                itemValues[j] = this.typecast(this.enumSource[i].value({
                  i: j,
                  item
                }))
                /* Use value directly */
              } else {
                itemValues[j] = items[j]
              }

              /* Rendered title */
              if (this.enumSource[i].title) {
                itemTitles[j] = this.enumSource[i].title({
                  i: j,
                  item
                })
                /* Use value as the title also */
              } else {
                itemTitles[j] = itemValues[j]
              }
            }

            if (this.enumSource[i].sort) {
              (((itemValues, itemTitles, order) => {
                itemValues.map((v, i) => ({
                  v,
                  t: itemTitles[i]
                })).sort((a, b) => (a.v < b.v) ? -order : ((a.v === b.v) ? 0 : order)).forEach((v, i) => {
                  itemValues[i] = v.v
                  itemTitles[i] = v.t
                })
              }).bind(null, itemValues, itemTitles, this.enumSource[i].sort === 'desc' ? 1 : -1))()
            }

            selectOptions = selectOptions.concat(itemValues)
            selectTitles = selectTitles.concat(itemTitles)
          }
        }
      }

      const prevValue = this.value

      this.theme.setSelectOptions(this.input, selectOptions, selectTitles)
      this.enum_options = selectOptions
      this.enum_display = selectTitles
      this.enum_values = selectOptions

      /* If the previous value is still in the new select options */
      /* or if global option "enum_source_value_auto_select" is true, stick with it */
      if (selectOptions.includes(prevValue) || this.jsoneditor.options.enum_source_value_auto_select !== false) {
        this.input.value = prevValue
        this.value = prevValue
        /* Otherwise, set the value to the first select option */
      } else {
        this.input.value = selectOptions[0]
        this.value = this.typecast(selectOptions[0] || '')
        if (this.parent && !this.watchLoop) this.parent.onChildEditorChange(this)
        else this.jsoneditor.onChange()
        this.jsoneditor.notifyWatchers(this.path)
      }

      /*  - learning labs code -  */
      // If the last value that was attempted to be set didn't exist in the options due to the options not being completely populated yet,
      // but now it exists in the options, set it now:
      if (this.valueThatDidntExistInOptions && selectOptions.includes(this.valueThatDidntExistInOptions)) {
        this.setValue(this.valueThatDidntExistInOptions)
        this.valueThatDidntExistInOptions = null
      }
      /*  ----------------------  */
    }

    super.onWatchedFieldChange()
  }
}

export default SelectEditor
